// src/utils/modificationIssue.ts — SCE Issues
// Édition d'un problème : ce qui peut changer, par qui, à quelles conditions,
// et comment le dire dans la timeline. Coordinateur (Lot 6, demande 8) et
// administrateur (Lot 7, demande 13 — « édition totale »).
//
// ─── Pourquoi un module pur ───
//
// La même règle sert deux fois : la fiche s'en sert pour griser « Enregistrer »
// et dire ce qui manque, l'API pour refuser d'écrire. Écrite ici une seule
// fois, elle ne peut pas diverger — et elle se teste sans réseau ni React.
//
// ─── Coordinateur (décision 5 du cadrage) ───
//
//   Ouverts   titre, description, phase, famille, criticité, métier,
//             équipement (01/10 : un équipement mal saisi se corrige à la
//             source, sans passer par l'administrateur)
//   Fermés    déclarant, date de déclaration, données de fin
//
//  - Un problème ANNULÉ ne se modifie plus.
//  - Le MÉTIER ne change que sur un problème actif, et remet famille et
//    criticité à « À qualifier » : ce sont des jugements du NOUVEAU métier.
//  - Famille et criticité ne s'éditent qu'une fois le problème qualifié, et
//    ne reviennent jamais à « À qualifier » par l'édition.
//  - Le SUJET (23/10, « Goulotte ») suit la règle du métier : actif seulement,
//    famille et criticité remises à qualifier (ses familles sont les siennes).
//    Un sujet impose son métier : Goulotte = MECA.
//
// ─── Administrateur (option A retenue le 25/09) ───
//
// Les mêmes champs sans restriction de statut, plus l'équipement (N° de série,
// OF, PN — catégorie recalculée) et les données de fin (commentaire de fin,
// badges d'un clos, motif d'un annulé). Il choisit lui-même famille et
// criticité, « À qualifier » compris : c'est le recours de dernier ressort.
// Restent verrouillés pour tous : déclarant (colonne système Author), date de
// déclaration, ID. La réouverture est une action à part (rouvrirIssue).
//
// ─── Traçabilité (décision 5, option B) ───
//
// Un enregistrement = un événement « Modification », une ligne par champ
// changé, « avant → après ». Les textes longs remplacés sont cités en entier.

import {
  A_QUALIFIER,
  ANNULE,
  CATEGORIES_PAR_SUFFIXE,
  CRITICITES_ORDRE,
  CRITICITES_TRANCHEES,
  PHASES_ORDRE,
  commentaireClotureObligatoire,
  erreurBadges,
  estActif,
  estMotifAnnulation,
  estTermine,
  ordonnerBadges,
  suffixeNumeroSerie,
  type BadgeCloture,
  type CategorieEquipement,
  type Criticite,
  type Issue,
  type Metier,
  type MotifAnnulation,
  type Phase,
  type Sujet,
} from '@/types/issue.types'
import { METIERS_LABELS, METIERS_ORDRE } from '@/config/metiers.config'
import { SUJETS_CONFIG, famillesDuProbleme } from '@/config/sujets.config'

/** Mêmes bornes que le formulaire de déclaration. */
export const TITRE_MAX = 120
export const DESCRIPTION_MAX = 500

/** Ce que le formulaire d'édition envoie : l'état complet des champs saisis. */
export interface SaisieModification {
  titre: string
  description: string
  phase: Phase
  famille: string
  criticite: Criticite
  metier: Metier
  /** Sujet (23/10) ; vide : aucun. */
  sujet: Sujet | ''
  // Administrateur seulement — ignorés pour un coordinateur.
  numeroSerie: string
  of: string
  pn: string
  badgesCloture: BadgeCloture[]
  motifAnnulation: MotifAnnulation | null
  commentaireCloture: string
}

/**
 * État modifiable complet : la saisie, plus ce qui s'en DÉDUIT — la catégorie
 * (suffixe du N° de série) et les références qualité (retirées avec leur
 * badge : une FNC-T sans badge FNC-T serait une donnée orpheline).
 */
export interface EtatModifiable extends SaisieModification {
  /** null : suffixe de N° de série inconnu — refusé à la validation. */
  familleEquipement: CategorieEquipement | null
  refFnct: string
  refNcs: string
}

export type ChampModifiable = keyof EtatModifiable

export interface Changement {
  champ: ChampModifiable
  /** Valeurs en texte, telles que la timeline les cite. */
  avant: string
  apres: string
}

export interface AnalyseModification {
  apres: EtatModifiable
  changements: Changement[]
  /** Motif du refus, ou null si la modification est recevable. */
  refus: string | null
}

export const LIBELLES_CHAMPS: Record<ChampModifiable, string> = {
  titre: 'Titre',
  description: 'Description',
  phase: 'Phase',
  famille: 'Famille',
  criticite: 'Criticité',
  metier: 'Métier',
  sujet: 'Sujet',
  numeroSerie: 'N° de série',
  of: 'OF',
  pn: 'PN',
  familleEquipement: 'Catégorie',
  badgesCloture: 'Badges de clôture',
  refFnct: 'Référence FNC-T',
  refNcs: 'Référence NC-S',
  motifAnnulation: 'Motif d\'annulation',
  commentaireCloture: 'Commentaire de fin',
}

/**
 * Ordre des lignes dans l'événement de timeline : l'identité du problème,
 * puis son aiguillage (le métier explique les deux lignes qui le suivent),
 * son équipement, sa fin, et les textes longs en dernier.
 */
const ORDRE_CHAMPS: readonly ChampModifiable[] = [
  'titre', 'metier', 'sujet', 'famille', 'criticite', 'phase',
  'numeroSerie', 'of', 'pn', 'familleEquipement',
  'badgesCloture', 'refFnct', 'refNcs', 'motifAnnulation',
  'description', 'commentaireCloture',
]

/** Champs dont la valeur est un texte libre : comparés sans espaces de bord. */
const TEXTES_LIBRES: ReadonlySet<ChampModifiable> = new Set(['titre', 'description', 'commentaireCloture'])

/** Pré-remplit le formulaire d'édition avec le problème tel qu'affiché. */
export function saisieDepuis(issue: Issue): SaisieModification {
  return {
    titre: issue.titre,
    description: issue.description,
    phase: issue.phase,
    famille: issue.famille,
    criticite: issue.criticite,
    metier: issue.metier,
    sujet: issue.sujet ?? '',
    numeroSerie: issue.numeroSerie,
    of: issue.of,
    pn: issue.pn,
    badgesCloture: issue.badgesCloture ?? [],
    motifAnnulation: issue.motifAnnulation ?? null,
    commentaireCloture: issue.commentaireCloture ?? '',
  }
}

function etatDepuis(issue: Issue): EtatModifiable {
  return {
    ...saisieDepuis(issue),
    familleEquipement: issue.familleEquipement,
    refFnct: issue.refFnct ?? '',
    refNcs: issue.refNcs ?? '',
  }
}

/** Valeur d'un champ en texte — pour comparer, et pour la timeline. */
export function enTexte(etat: EtatModifiable, champ: ChampModifiable): string {
  const v = etat[champ]
  if (Array.isArray(v)) return v.join(' · ')
  return v ?? ''
}

/** Catégorie déduite du suffixe — même table que la déclaration. */
function categorieDe(numeroSerie: string): CategorieEquipement | null {
  return CATEGORIES_PAR_SUFFIXE[suffixeNumeroSerie(numeroSerie)] ?? null
}

/**
 * Analyse une saisie : l'état qui serait écrit, les champs qui changent (dans
 * l'ordre de la timeline), et le motif de refus éventuel.
 *
 * Pour un coordinateur, les champs réservés à l'administrateur (données de
 * fin) sont REPRIS du problème, quelle que soit la saisie : ils ne peuvent
 * pas changer. L'équipement, lui, est ouvert à tous.
 */
export function analyserModification(
  avant: Issue,
  saisie: SaisieModification,
  { admin }: { admin: boolean },
): AnalyseModification {
  const a = etatDepuis(avant)
  const s: SaisieModification = admin
    ? saisie
    : {
        ...saisie,
        badgesCloture: a.badgesCloture,
        motifAnnulation: a.motifAnnulation,
        commentaireCloture: a.commentaireCloture,
      }

  // Un sujet impose son métier (Goulotte : MECA), quelle que soit la saisie.
  const metier = s.sujet ? SUJETS_CONFIG[s.sujet].metier : s.metier
  const aiguillageChange = metier !== a.metier || s.sujet !== a.sujet
  // Coordinateur : changer le métier ou le sujet REMET famille et criticité à
  // qualifier. Administrateur : il les choisit lui-même dans le même formulaire.
  const resetQualification = aiguillageChange && !admin
  const numeroSerie = s.numeroSerie.trim()
  const badges = ordonnerBadges(s.badgesCloture)

  const apres: EtatModifiable = {
    titre: s.titre.trim(),
    description: s.description.trim(),
    phase: s.phase,
    metier,
    sujet: s.sujet,
    famille: resetQualification ? A_QUALIFIER : s.famille,
    criticite: resetQualification ? A_QUALIFIER : s.criticite,
    numeroSerie,
    of: s.of.trim(),
    pn: s.pn.trim(),
    // Recalculée seulement si l'équipement change : une catégorie historique
    // (ligne créée hors application) n'est pas « corrigée » à l'insu de qui
    // modifie un titre.
    familleEquipement: numeroSerie === a.numeroSerie ? a.familleEquipement : categorieDe(numeroSerie),
    badgesCloture: badges,
    refFnct: badges.includes('FNC-T') ? a.refFnct : '',
    refNcs: badges.includes('NC-S') ? a.refNcs : '',
    motifAnnulation: s.motifAnnulation,
    commentaireCloture: s.commentaireCloture.trim(),
  }

  const changements: Changement[] = []
  for (const champ of ORDRE_CHAMPS) {
    const texteAvant = enTexte(a, champ)
    // L'ancienne valeur d'un texte libre est comparée nettoyée elle aussi,
    // sinon une description terminée par un retour à la ligne serait
    // « modifiée » à chaque enregistrement.
    const comparable = TEXTES_LIBRES.has(champ) ? texteAvant.trim() : texteAvant
    const texteApres = enTexte(apres, champ)
    if (comparable !== texteApres) {
      // L'avant est gardé TEL QU'IL ÉTAIT : c'est ce que la timeline cite.
      changements.push({ champ, avant: texteAvant, apres: texteApres })
    }
  }

  return { apres, changements, refus: refuser(avant, apres, changements, admin) }
}

function refuser(
  issue: Issue,
  apres: EtatModifiable,
  changements: readonly Changement[],
  admin: boolean,
): string | null {
  if (issue.statut === ANNULE && !admin) return 'Un problème annulé ne se modifie plus.'
  if (changements.length === 0) return 'Aucune modification à enregistrer.'

  const change = new Set(changements.map((c) => c.champ))
  const metierChange = change.has('metier')
  const sujetChange = change.has('sujet')
  const aiguillageChange = metierChange || sujetChange
  const familles = famillesDuProbleme({ metier: apres.metier, ...(apres.sujet ? { sujet: apres.sujet } : {}) })
  const nomFamilles = apres.sujet ? `du sujet ${apres.sujet}` : `du métier ${apres.metier}`

  if (sujetChange && !admin && !estActif(issue)) {
    return `Le sujet d'un problème ${issue.statut.toLowerCase()} ne se change plus.`
  }

  if (metierChange) {
    if (!(METIERS_ORDRE as readonly string[]).includes(apres.metier)) {
      return `Métier inconnu : « ${apres.metier} ».`
    }
    if (!admin && !estActif(issue)) {
      return `Le métier d'un problème ${issue.statut.toLowerCase()} ne se change plus.`
    }
  }

  // Administrateur qui change le métier ou le sujet : la famille doit
  // appartenir au NOUVEAU, même s'il ne l'a pas retouchée — une famille MECA
  // sur un problème PROCESS serait une donnée incohérente.
  if (admin && aiguillageChange && apres.famille !== A_QUALIFIER && !familles.includes(apres.famille)) {
    return `Choisissez une famille ${nomFamilles} (ou « ${A_QUALIFIER} »).`
  }

  for (const c of changements) {
    switch (c.champ) {
      case 'titre':
        if (apres.titre === '') return 'Le titre est obligatoire.'
        if (apres.titre.length > TITRE_MAX) return `Le titre dépasse ${TITRE_MAX} caractères.`
        break
      case 'description':
        if (apres.description.length > DESCRIPTION_MAX) {
          return `La description dépasse ${DESCRIPTION_MAX} caractères.`
        }
        break
      case 'phase':
        if (!(PHASES_ORDRE as readonly string[]).includes(apres.phase)) {
          return `Phase inconnue : « ${apres.phase} ».`
        }
        break
      case 'metier':
      case 'sujet':
        break
      case 'famille':
      case 'criticite': {
        if (admin) {
          if (c.champ === 'famille' && apres.famille !== A_QUALIFIER && !familles.includes(apres.famille)) {
            return `Famille inconnue ${nomFamilles} : « ${apres.famille} ».`
          }
          if (c.champ === 'criticite' && !CRITICITES_ORDRE.includes(apres.criticite)) {
            return `Criticité inconnue : « ${apres.criticite} ».`
          }
          break
        }
        // Coordinateur. Remise à zéro par un changement de métier ou de sujet : voulue.
        if (aiguillageChange) break
        if (issue.famille === A_QUALIFIER || issue.criticite === A_QUALIFIER) {
          return 'Famille et criticité se renseignent d\'abord par la qualification.'
        }
        if (c.apres === A_QUALIFIER) {
          return `La ${LIBELLES_CHAMPS[c.champ].toLowerCase()} ne peut pas revenir à « ${A_QUALIFIER} ».`
        }
        if (c.champ === 'famille' && !familles.includes(apres.famille)) {
          return `Famille inconnue ${nomFamilles} : « ${apres.famille} ».`
        }
        if (c.champ === 'criticite' && !(CRITICITES_TRANCHEES as readonly string[]).includes(apres.criticite)) {
          return `Criticité inconnue : « ${apres.criticite} ».`
        }
        break
      }
      case 'numeroSerie':
      case 'of':
      case 'pn':
      case 'familleEquipement':
        if (apres.numeroSerie === '') return 'Le N° de série est obligatoire.'
        if (apres.familleEquipement === null) {
          return `N° de série « ${apres.numeroSerie} » : suffixe inconnu, catégorie impossible à déduire.`
        }
        break
      // ─── Administrateur seulement ───
      case 'badgesCloture': {
        if (!admin) return 'Les badges de clôture ne se modifient que par l\'administrateur.'
        if (issue.statut !== 'Clos') return 'Seul un problème clos porte des badges de clôture.'
        const refus = erreurBadges(apres.badgesCloture)
        if (refus) return refus
        break
      }
      case 'refFnct':
      case 'refNcs':
        // Déduites : retirées avec leur badge, rien d'autre à vérifier.
        break
      case 'motifAnnulation':
        if (!admin) return 'Le motif d\'annulation ne se modifie que par l\'administrateur.'
        if (issue.statut !== ANNULE) return 'Seul un problème annulé porte un motif d\'annulation.'
        if (!estMotifAnnulation(apres.motifAnnulation)) return 'Motif d\'annulation inconnu.'
        break
      case 'commentaireCloture':
        if (!admin) return 'Le commentaire de fin ne se modifie que par l\'administrateur.'
        if (!estTermine(issue)) return 'Seul un problème clos ou annulé porte un commentaire de fin.'
        // Un annulé garde son commentaire obligatoire ; un clos ne l'a que
        // avec « Autre » (02/10).
        if (apres.commentaireCloture === '' && (issue.statut === ANNULE || commentaireClotureObligatoire(apres.badgesCloture))) {
          return 'Le commentaire de fin ne peut pas être vidé.'
        }
        break
      default: {
        const nonTraite: never = c.champ
        return `Champ non modifiable : ${String(nonTraite)}.`
      }
    }
  }
  return null
}

/**
 * Corps de l'événement « Modification » de la timeline : une ligne par champ.
 *
 * Description et commentaire de fin ne sont pas réécrits en entier (le
 * nouveau texte est sur la fiche) ; l'ANCIEN est cité en entier — c'est le
 * seul endroit où il survit, et pour un support qualité, perdre la version
 * d'origine d'un constat n'est pas acceptable.
 *
 * @param suiviPar noms des coordinateurs du nouveau métier (ou sujet), s'il change
 */
export function resumerModification(
  changements: readonly Changement[],
  suiviPar?: readonly string[],
): string {
  const lignes: string[] = []
  const vide = (v: string): string => (v === '' ? '—' : v)
  for (const c of changements) {
    const libelle = LIBELLES_CHAMPS[c.champ]
    switch (c.champ) {
      case 'titre':
        lignes.push(`${libelle} : « ${c.avant} » → « ${c.apres} »`)
        break
      case 'metier': {
        const suivi = suiviPar && suiviPar.length > 0 ? ` — suivi par ${suiviPar.join(', ')}` : ''
        lignes.push(`${libelle} : ${libelleMetier(c.avant)} → ${libelleMetier(c.apres)}${suivi}`)
        break
      }
      case 'sujet': {
        // « suivi par » une seule fois : sur la ligne du métier s'il change aussi.
        const metierAussi = changements.some((x) => x.champ === 'metier')
        const suivi = !metierAussi && suiviPar && suiviPar.length > 0 ? ` — suivi par ${suiviPar.join(', ')}` : ''
        lignes.push(c.apres === '' ? `${libelle} « ${c.avant} » retiré${suivi}` : `${libelle} : ${vide(c.avant)} → ${c.apres}${suivi}`)
        break
      }
      case 'refFnct':
      case 'refNcs':
        lignes.push(
          c.apres === ''
            ? `${libelle} retirée avec son badge (était ${c.avant})`
            : `${libelle} : ${vide(c.avant)} → ${c.apres}`,
        )
        break
      case 'description':
      case 'commentaireCloture': {
        const genre = c.champ === 'description' ? 'e' : ''
        lignes.push(
          c.avant.trim() === ''
            ? `${libelle} ajouté${genre}.`
            : `${libelle} ${c.apres === '' ? `retiré${genre}` : `modifié${genre}`}. Ancienne version :\n« ${c.avant.trim()} »`,
        )
        break
      }
      default:
        lignes.push(`${libelle} : ${vide(c.avant)} → ${vide(c.apres)}`)
    }
  }
  return lignes.join('\n')
}

// ─── Réouverture (administrateur) ───

/**
 * Statut d'un problème rouvert : `Ouvert` s'il attend encore sa qualification
 * (un annulé garde « À qualifier »), `En cours` sinon — la même règle que la
 * qualification, qui fait passer Ouvert → En cours.
 */
export function statutReouverture(issue: Pick<Issue, 'famille' | 'criticite'>): 'Ouvert' | 'En cours' {
  return issue.famille === A_QUALIFIER || issue.criticite === A_QUALIFIER ? 'Ouvert' : 'En cours'
}

/**
 * Corps de l'événement de réouverture. Les données de fin sont EFFACÉES de la
 * fiche : elles sont donc citées ici en entier, pour que rien ne se perde.
 */
export function resumerReouverture(issue: Issue, commentaire: string): string {
  const efface: string[] = []
  if (issue.dateCloture) efface.push(`Date de clôture : ${issue.dateCloture.slice(0, 10)}`)
  if (issue.dateAnnulation) efface.push(`Date d'annulation : ${issue.dateAnnulation.slice(0, 10)}`)
  if (issue.motifAnnulation) efface.push(`Motif d'annulation : ${issue.motifAnnulation}`)
  if (issue.badgesCloture?.length) efface.push(`Badges de clôture : ${issue.badgesCloture.join(' · ')}`)
  if (issue.refFnct) efface.push(`Référence FNC-T : ${issue.refFnct}`)
  if (issue.refNcs) efface.push(`Référence NC-S : ${issue.refNcs}`)
  if (issue.commentaireCloture) efface.push(`Commentaire de fin : « ${issue.commentaireCloture} »`)
  return (
    `Problème rouvert.\n\n${commentaire.trim()}` +
    (efface.length > 0 ? `\n\nDonnées de fin effacées :\n${efface.map((l) => `• ${l}`).join('\n')}` : '')
  )
}

function libelleMetier(code: string): string {
  return (METIERS_LABELS as Record<string, string>)[code] ?? code
}
